import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Users, Building, Target, Calendar, BarChart3, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';

const OnboardingFlow = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState({
    role: '',
    companySize: '',
    industry: '',
    primaryGoal: '',
    experience: ''
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();

  const steps = [
    {
      id: 'welcome',
      title: t('onboarding.welcomeTitle','Welcome to Bissa.ai!'),
      subtitle: t('onboarding.welcomeSubtitle','Let\'s get you set up for success'),
      component: WelcomeStep
    },
    {
      id: 'role',
      title: t('onboarding.roleTitle','What\'s your role?'),
      subtitle: t('onboarding.roleSubtitle','Help us personalize your experience'),
      component: RoleStep
    },
    {
      id: 'company',
      title: t('onboarding.companyTitle','Tell us about your company'),
      subtitle: t('onboarding.companySubtitle','This helps us customize features for you'),
      component: CompanyStep
    },
    {
      id: 'goals',
      title: t('onboarding.goalsTitle','What\'s your primary goal?'),
      subtitle: t('onboarding.goalsSubtitle','We\'ll focus on features that matter most'),
      component: GoalsStep
    },
    {
      id: 'setup',
      title: t('onboarding.setupTitle','Let\'s create your first records'),
      subtitle: t('onboarding.setupSubtitle','Get started with some sample data'),
      component: SetupStep
    },
    {
      id: 'complete',
      title: t('onboarding.completeTitle','You\'re all set!'),
      subtitle: t('onboarding.completeSubtitle','Welcome to the Bissa.ai community'),
      component: CompleteStep
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    // Save onboarding completion to localStorage (per user)
    if (user?.email) {
      const userKey = `bissa_onboarding_completed_${user.email}`;
      localStorage.setItem(userKey, 'true');
      localStorage.setItem('bissa_user_data', JSON.stringify(userData));
      
      // Also save to database so it persists across devices/browsers and logouts
      try {
        const response = await authFetch(`${API_BASE}/api/users/onboarding-complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          console.warn('Failed to save onboarding completion to database, but localStorage saved');
        } else {
          console.log('Onboarding completion saved to database');
        }
      } catch (err) {
        console.warn('Error saving onboarding completion to database:', err);
        // Continue anyway - localStorage is saved
      }
    }
    
    onComplete();
    onClose();
  };
  
  const handleClose = () => {
    // If user closes without completing, don't mark as completed
    // It will show again on next login if not completed
    onClose();
  };

  if (!isOpen) return null;

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-bissa-blue to-bissa-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <div>
                <h2 className="text-xl font-bold">{steps[currentStep].title}</h2>
                <p className="text-blue-100 text-sm">{steps[currentStep].subtitle}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-blue-100">{t('onboarding.step','Step')} {currentStep + 1} {t('onboarding.of','of')} {steps.length}</span>
              <span className="text-sm text-blue-100">{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
            </div>
            <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
              <div 
                className="bg-bissa-gold h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <CurrentStepComponent
            userData={userData}
            setUserData={setUserData}
            onNext={nextStep}
            onPrev={prevStep}
            onComplete={handleComplete}
            user={user}
            navigate={navigate}
            isFirst={currentStep === 0}
            isLast={currentStep === steps.length - 1}
            t={t}
          />
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              currentStep === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('onboarding.back','Back')}
          </button>

          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-bissa-blue' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={currentStep === steps.length - 1 ? handleComplete : nextStep}
            className="flex items-center gap-2 px-6 py-2 bg-bissa-blue text-white rounded-lg hover:bg-bissa-gold hover:text-bissa-blue transition-colors font-medium"
          >
            {currentStep === steps.length - 1 ? (
              <>
                <Check className="w-4 h-4" />
                {t('onboarding.getStarted','Get Started')}
              </>
            ) : (
              <>
                {t('onboarding.next','Next')}
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Individual Step Components
const WelcomeStep = ({ user, isFirst, t }) => (
  <div className="text-center py-8">
    <div className="w-20 h-20 bg-gradient-to-br from-bissa-blue to-bissa-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
      <Zap className="w-10 h-10 text-white" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-4">
      {t('onboarding.welcomeMessage', `Welcome${user?.name ? `, ${user.name}` : ''}!`)}
    </h3>
    <p className="text-gray-600 leading-relaxed max-w-md mx-auto mb-6">
      {t('onboarding.welcomeDescription', 'Just as the Bissa people built thriving communities through strong relationships, we\'ll help you build lasting business connections with AI-powered insights.')}
    </p>
    <div className="bg-bissa-blue-50 p-4 rounded-lg border border-bissa-blue-200">
      <p className="text-bissa-blue font-medium">{t('onboarding.timeEstimate', '✨ This will take about 2 minutes')}</p>
    </div>
  </div>
);

const RoleStep = ({ userData, setUserData }) => {
  const roles = [
    { id: 'founder', label: 'Founder/CEO', icon: Target },
    { id: 'sales', label: 'Sales Professional', icon: BarChart3 },
    { id: 'marketing', label: 'Marketing Manager', icon: Users },
    { id: 'business_dev', label: 'Business Development', icon: Building },
    { id: 'freelancer', label: 'Freelancer/Consultant', icon: Calendar },
    { id: 'other', label: 'Other', icon: Users }
  ];

  return (
    <div className="py-4">
      <div className="grid grid-cols-2 gap-4 mb-6">
        {roles.map((role) => {
          const IconComponent = role.icon;
          return (
            <button
              key={role.id}
              onClick={() => setUserData({ ...userData, role: role.id })}
              className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                userData.role === role.id
                  ? 'border-bissa-blue bg-bissa-blue-50 text-bissa-blue'
                  : 'border-gray-200 hover:border-bissa-blue-300'
              }`}
            >
              <IconComponent className={`w-6 h-6 mb-2 ${
                userData.role === role.id ? 'text-bissa-blue' : 'text-gray-400'
              }`} />
              <span className="font-medium">{role.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CompanyStep = ({ userData, setUserData }) => {
  const companySizes = [
    { id: 'solo', label: 'Just me (1)', desc: 'Solo entrepreneur' },
    { id: 'small', label: '2-10 people', desc: 'Small team' },
    { id: 'medium', label: '11-50 people', desc: 'Growing company' },
    { id: 'large', label: '50+ people', desc: 'Established business' }
  ];

  const industries = [
    'Technology', 'Professional Services', 'Real Estate', 'Healthcare',
    'Financial Services', 'Education', 'Retail', 'Manufacturing',
    'Nonprofit', 'Other'
  ];

  return (
    <div className="py-4 space-y-6">
      <div>
        <h4 className="font-semibold text-gray-900 mb-4">Company Size</h4>
        <div className="grid grid-cols-1 gap-3">
          {companySizes.map((size) => (
            <button
              key={size.id}
              onClick={() => setUserData({ ...userData, companySize: size.id })}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                userData.companySize === size.id
                  ? 'border-bissa-blue bg-bissa-blue-50'
                  : 'border-gray-200 hover:border-bissa-blue-300'
              }`}
            >
              <div className="font-medium">{size.label}</div>
              <div className="text-sm text-gray-500">{size.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-4">Industry</h4>
        <select
          value={userData.industry}
          onChange={(e) => setUserData({ ...userData, industry: e.target.value })}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bissa-blue focus:border-transparent"
        >
          <option value="">Select your industry</option>
          {industries.map((industry) => (
            <option key={industry} value={industry}>{industry}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

const GoalsStep = ({ userData, setUserData }) => {
  const goals = [
    { id: 'organize_contacts', label: 'Organize my contacts better', desc: 'Keep track of all business relationships' },
    { id: 'increase_sales', label: 'Increase sales and revenue', desc: 'Convert more leads to customers' },
    { id: 'improve_follow_up', label: 'Improve follow-up processes', desc: 'Never miss important opportunities' },
    { id: 'team_collaboration', label: 'Better team collaboration', desc: 'Share customer data across the team' },
    { id: 'automate_workflows', label: 'Automate repetitive tasks', desc: 'Save time on manual processes' },
    { id: 'track_performance', label: 'Track sales performance', desc: 'Measure and improve results' }
  ];

  return (
    <div className="py-4">
      <div className="space-y-3 mb-6">
        {goals.map((goal) => (
          <button
            key={goal.id}
            onClick={() => setUserData({ ...userData, primaryGoal: goal.id })}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              userData.primaryGoal === goal.id
                ? 'border-bissa-blue bg-bissa-blue-50'
                : 'border-gray-200 hover:border-bissa-blue-300'
            }`}
          >
            <div className="font-medium mb-1">{goal.label}</div>
            <div className="text-sm text-gray-500">{goal.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const SetupStep = ({ navigate }) => {
  const [selectedActions, setSelectedActions] = useState([]);

  const setupActions = [
    { id: 'create_contact', label: 'Add your first contact', desc: 'Start building your network' },
    { id: 'create_company', label: 'Add a company', desc: 'Track business relationships' },
    { id: 'create_deal', label: 'Create your first deal', desc: 'Start your sales pipeline' },
    { id: 'explore_features', label: 'Take a quick tour', desc: 'Learn key features' }
  ];

  const toggleAction = (actionId) => {
    setSelectedActions(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  return (
    <div className="py-4">
      <p className="text-gray-600 mb-6">
        Choose what you'd like to do first. You can always come back to these later.
      </p>
      <div className="space-y-3">
        {setupActions.map((action) => (
          <button
            key={action.id}
            onClick={() => toggleAction(action.id)}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              selectedActions.includes(action.id)
                ? 'border-bissa-blue bg-bissa-blue-50'
                : 'border-gray-200 hover:border-bissa-blue-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                selectedActions.includes(action.id)
                  ? 'border-bissa-blue bg-bissa-blue'
                  : 'border-gray-300'
              }`}>
                {selectedActions.includes(action.id) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <div>
                <div className="font-medium">{action.label}</div>
                <div className="text-sm text-gray-500">{action.desc}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const CompleteStep = ({ userData, navigate }) => (
  <div className="text-center py-8">
    <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
      <Check className="w-10 h-10 text-white" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-4">
      Welcome to the Bissa.ai community!
    </h3>
    <p className="text-gray-600 leading-relaxed max-w-md mx-auto mb-8">
      You're now part of a community that believes in building strong, lasting business 
      relationships through trust and intelligent connection.
    </p>
    
    <div className="bg-gradient-to-r from-bissa-blue-50 to-bissa-gold-50 p-6 rounded-xl border border-bissa-blue-200 mb-6">
      <h4 className="font-semibold text-bissa-blue mb-2">🚀 Ready to get started?</h4>
      <p className="text-bissa-blue-700 text-sm">
        Your dashboard is ready with personalized recommendations based on your goals.
      </p>
    </div>

    <div className="text-sm text-gray-500">
      Need help? Check out our help center or contact support anytime.
    </div>
  </div>
);

export default OnboardingFlow;